/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { atom, useRecoilState, useRecoilValue } from "recoil";
import { IntegrationAPI } from "../../../api/IntegrationAPI";
import {
  createLearnisticIntegration,
  deleteLearnisticIntegration,
  updateLearnisticContacts,
} from "../../../api/Learnistic";
import { UserAtom } from "../../../atoms/UserAtom";
import { ILearnisticIntegration } from "../../../generated/Api";
import GenericPasswordInput from "../../GenericPasswordInput";
import Warning from "../../Modal/container/Warning";
import { useModal } from "../../Modal/Modal";
import PhoneInput from "../../PhoneInput";
import StyledButton from "../../StyledButton";
import { AllIntegrationsAtom } from "./Integrations";
import LearnisticTagPicker from "./LearnisticAppPicker";
import { useEffect } from "react";

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  height: fit-content;

  margin: 40px 0px;
  padding: 48px;

  border-radius: 4px;
  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  cursor: default;

  .e1s4q0d32 {
    input {
      height: 40px;
      border-color: rgb(236, 238, 239);
    }
  }

  @media screen and (max-width: 1140px) {
    padding: 48px 24px;
  }
`;

const TitleWrapper = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;
  grid-gap: 32px;

  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 24px;
  margin-bottom: 24px;

  img {
    width: 67px;
    height: 67px;
  }
`;

const MainTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 18px;
`;

const SecondaryTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 16px;

  display: flex;
`;

const MainText = styled.div`
  color: #707070;
  font-weight: 400;
  font-size: 15px;
`;

const TextWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  grid-gap: 8px;

  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 24px;
  margin-bottom: 24px;

  border-top: 1px solid #eaeaeabf;
  padding-top: 24px;
  margin-top: 24px;

  &:first-of-type {
    border-top: unset;
    padding-top: unset;
    margin-top: unset;
    border-bottom: unset;
    padding-bottom: unset;
    margin-bottom: unset;
  }
`;

const FormContent = styled.div`
  width: 100%;
  height: fit-content;

  padding: 0 24px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  .flex-column-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    grid-gap: 8px;
  }
`;

//!

const LoginCard = styled.div`
  width: fit-content;
  height: fit-content;

  margin: 0 auto;
  padding: 32px 24px;

  border-radius: 8px;
  border: 1px solid #eaeaeabf;

  display: flex;
  flex-direction: column;
  grid-gap: 20px;

  .header-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    grid-gap: 16px;

    img {
      width: 64px;
      height: 64px;
    }

    h1 {
      margin: 0;
      padding: 0;

      font-size: 18px;
    }
  }
`;

const FormRow = styled.div`
  width: 100%;

  margin-top: 20px;

  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

export default function Learnistic(): JSX.Element | null {
  const [passcode, setPasscode] = useState("");

  const [integrations, setIntegrations] = useRecoilState(AllIntegrationsAtom);

  const learnisticIntegration = integrations.find(
    (i) => i.type === "LEARNISTIC"
  ) as ILearnisticIntegration;

  const router = useRouter();

  const user = useRecoilValue(UserAtom);
  const [countryCode, setCountryCode] = useState("+1");
  const [phone, setPhone] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const modal = useModal();

  async function submit() {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const response = await createLearnisticIntegration({
        phoneNumber: (countryCode + phone)
          .replace("+", "")
          .split("-")
          .join("")
          .split("(")
          .join("")
          .split(")")
          .join("")
          .split(" ")
          .join(""),
        passcode,
      });

      if (response) {
        // Update integrations
        let integrations = await IntegrationAPI.getAll();
        if (integrations) setIntegrations(integrations);

        modal.pushModal(
          <Warning
            icon={"./icons/disconnect.svg"}
            title="Learnistic Integration"
            description={`To improve the integration with Learnistic we would like to update the phone number records in your BerserkerMail account. We will not overwrite any existing phone numbers. Click yes to update or no to skip this.`}
            onCancelClicked={() => modal.popModal()}
            onOkClicked={async () => {
              updateLearnisticContacts();
              modal.popModal();
            }}
            buttonsName={["No", "Yes"]}
          />
        );
      } else {
        toast("Wrong phone and/or passcode", { type: "error" });
      }
    } catch (err) {
      console.log(err);
      toast("Wrong phone and/or passcode", { type: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  async function removeIntegration() {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const deleteIntegration = await deleteLearnisticIntegration();
      if (deleteIntegration) {
        setIntegrations(integrations.filter((i) => i.type !== "LEARNISTIC"));
        router.replace("/settings?screen=INTEGRATIONS");
      } else {
        toast("Unexpected Error", { type: "error" });
      }
    } catch (err) {
      console.log("error", err);
      toast("Unexpected Error", { type: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        submit();
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [passcode, countryCode, phone]);

  return (
    <Container>
      <TitleWrapper>
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            grid-gap: 16px;
          `}
        >
          <img src="./images/learnistic.png" alt="learnistic" />
          <TextWrapper>
            <MainTitle>Learnistic Integration</MainTitle>
            <MainText>
              {learnisticIntegration
                ? "Please choose the app you wish to configure from the list below."
                : "To integrate BerserkerMail with your Learnistic account, please login with your Learnistic credentials below."}
            </MainText>
          </TextWrapper>
        </div>

        {learnisticIntegration ? (
          <StyledButton
            isLoading={isLoading}
            onClick={() => {
              modal.pushModal(
                <Warning
                  icon={"./icons/disconnect.svg"}
                  title="Disconnect Learnistic?"
                  description={`Are you sure you want to disconnect from Learnistic?\nBy disconnecting this app, your webhooks will no longer work and any tags you have implemented will be lost.`}
                  onCancelClicked={() => modal.popModal()}
                  onOkClicked={async () => {
                    await removeIntegration();
                    modal.popModal();
                  }}
                  buttonsName={["No", "Yes"]}
                />
              );
            }}
            inverted
            css={css`
              height: 45px;
            `}
          >
            Disconnect
          </StyledButton>
        ) : (
          <div />
        )}
      </TitleWrapper>

      {!learnisticIntegration ? (
        <LoginCard>
          <div className="header-card">
            <img src="./images/learnistic.png" alt="learnistic" />
            <h1>Log in with your Learnistic account</h1>
          </div>

          <PhoneInput
            countryCode={countryCode}
            phone={phone}
            handleCountryCode={setCountryCode}
            handlePhone={setPhone}
          />
          <GenericPasswordInput onChange={setPasscode} />
          <StyledButton onClick={submit} isLoading={isLoading}>
            Login
          </StyledButton>
        </LoginCard>
      ) : (
        <FormContent>
          <div className="flex-column-wrapper">
            <SecondaryTitle>Configuration</SecondaryTitle>
            <MainText>
              {learnisticIntegration.args.maxIntegrations ===
              learnisticIntegration.args.vAppIds.length
                ? learnisticIntegration.args.maxIntegrations > 1
                  ? "Those are your integrated apps"
                  : "This is your integrated app"
                : `Choose your app${
                    learnisticIntegration.args.maxIntegrations > 1 ? "s" : ""
                  } to be integrated`}
            </MainText>
          </div>

          <FormRow>
            <LearnisticTagPicker
              readOnly={
                learnisticIntegration.args.maxIntegrations ===
                learnisticIntegration.args.vAppIds.length
              }
            />
          </FormRow>
        </FormContent>
      )}
    </Container>
  );
}
